/**
 * CreateRoleModal - Modale de création d'un rôle.
 *
 * Champs : libellé (autofocus, obligatoire), nom technique (auto-slug + override),
 * couleur (palette + hex), description.
 */

import React, { useState, useEffect } from 'react';
import { CloseIcon } from '../../icons';
import {
  COLOR_PALETTE,
  API_BASE,
  authHeaders,
  slugify,
  ROLE_NAME_REGEX
} from './types';

interface CreateRoleModalProps {
  onClose: () => void;
  onCreated: (newRoleId: string) => void;
}

function CreateRoleModal({ onClose, onCreated }: CreateRoleModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [name, setName] = useState('');
  const [nameOverridden, setNameOverridden] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [color, setColor] = useState<string>(COLOR_PALETTE[9] || '#3b82f6'); // bleu par défaut
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-slug tant que l'utilisateur n'a pas overrride manuellement
  useEffect(() => {
    if (!nameOverridden) {
      setName(slugify(displayName));
    }
  }, [displayName, nameOverridden]);

  function handleNameChange(value: string) {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setName(cleaned);
    setNameOverridden(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    if (!displayName.trim()) {
      setError('Le libellé est obligatoire.');
      return;
    }
    if (!name || !ROLE_NAME_REGEX.test(name)) {
      setError('Le nom technique doit commencer par une lettre minuscule et ne contenir que des lettres minuscules, chiffres et underscores.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      console.log('[CreateRoleModal] POST /api/roles', { name, display_name: displayName });
      const res = await fetch(`${API_BASE}/api/roles`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name,
          display_name: displayName.trim(),
          color,
          description: description.trim() || null,
          is_default: false
        })
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      const newId = json.role?.id;
      if (!newId) {
        throw new Error('Réponse serveur invalide : id manquant.');
      }
      console.log('[CreateRoleModal] Rôle créé', newId);
      onCreated(newId);
    } catch (err) {
      console.error('[CreateRoleModal] Erreur création:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        style={modalBox}
        onClick={e => e.stopPropagation()}
      >
        <div style={modalHeader}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            Créer un rôle
          </div>
          <button
            type="button"
            onClick={onClose}
            style={modalCloseBtn}
            title="Fermer"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Libellé */}
          <div>
            <label style={labelStyle}>
              Libellé <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Ex : Responsable de site"
              autoFocus
              required
              style={inputStyle}
            />
          </div>

          {/* Nom technique */}
          <div>
            <label style={labelStyle}>Nom technique</label>
            {!editingName ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: '#f1f5f9',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#475569',
                fontFamily: 'monospace'
              }}>
                <span style={{ flex: 1 }}>{name || <span style={{ color: '#94a3b8' }}>(automatique)</span>}</span>
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    backgroundColor: 'white',
                    color: '#3b82f6',
                    border: '1px solid #93c5fd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  modifier
                </button>
              </div>
            ) : (
              <input
                type="text"
                value={name}
                onChange={e => handleNameChange(e.target.value)}
                placeholder="ex : responsable_site"
                autoFocus
                style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '13px' }}
              />
            )}
            <div style={hintStyle}>
              {nameOverridden
                ? 'Lettres minuscules, chiffres et underscores. Doit commencer par une lettre.'
                : 'Calculé automatiquement depuis le libellé. Cliquez sur "modifier" pour personnaliser.'}
            </div>
          </div>

          {/* Couleur */}
          <div>
            <label style={labelStyle}>Couleur</label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {COLOR_PALETTE.map(c => {
                const isSelected = color === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: c,
                      border: isSelected ? '3px solid #3b82f6' : '2px solid #e2e8f0',
                      cursor: 'pointer',
                      padding: 0,
                      outline: 'none',
                      boxShadow: isSelected ? '0 0 0 2px white inset' : 'none'
                    }}
                    title={c}
                  />
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Description optionnelle..."
              rows={2}
              style={{
                ...inputStyle,
                resize: 'vertical',
                minHeight: '52px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {error && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={modalFooter}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting || !displayName.trim()}
            style={{
              padding: '8px 16px',
              backgroundColor: submitting || !displayName.trim() ? '#93c5fd' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: submitting || !displayName.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Création...' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const modalBox: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
  maxWidth: '480px',
  width: '100%',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const modalHeader: React.CSSProperties = {
  padding: '14px 20px',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

const modalFooter: React.CSSProperties = {
  padding: '12px 20px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  backgroundColor: '#f8fafc'
};

const modalCloseBtn: React.CSSProperties = {
  width: '28px',
  height: '28px',
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  cursor: 'pointer',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '6px'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const hintStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#94a3b8',
  marginTop: '4px'
};

export default CreateRoleModal;
